import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type {
  CalculationSetListItem,
  CalculationSetDetail,
  CalculationSetCargosResponse,
} from "../entities/calculation-set";
import type { Calculation, CalculationTrip } from "../entities/calculation";
import type {
  CreateCalculationDto,
  UpdateCalculationDto,
  CreateCalculationSetDto,
  UpdateCalculationSetDto,
  UpdateCalculationSetStatusDto,
  AssignSetCargosPayload,
  CalculationTripInput,
  CalculationTripSyncInput,
} from "./types";

export function createCalculationsRepository(fetcher: Fetcher) {
  return {
    // ─── Calculation Sets ────────────────────────────────────────────────
    async listSets(quoteId: string): Promise<{ items: CalculationSetListItem[]; total: number }> {
      const response = await fetcher<ApiCollectionResponse<CalculationSetListItem>>(
        `/quotes/${quoteId}/calculation-sets`,
      );
      const items = response.data?.items ?? [];
      return {
        items,
        total: response.data?.pagination?.total ?? items.length,
      };
    },

    async getSet(quoteId: string, setId: string): Promise<CalculationSetDetail> {
      const response = await fetcher<ApiSingleResponse<CalculationSetDetail>>(
        `/quotes/${quoteId}/calculation-sets/${setId}`,
      );
      return response.data;
    },

    async createSet(quoteId: string, payload: CreateCalculationSetDto): Promise<{ id: string }> {
      const response = await fetcher<ApiSingleResponse<{ id: string }>>(
        `/quotes/${quoteId}/calculation-sets`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateSet(
      quoteId: string,
      setId: string,
      payload: UpdateCalculationSetDto,
    ): Promise<CalculationSetDetail> {
      const response = await fetcher<ApiSingleResponse<CalculationSetDetail>>(
        `/quotes/${quoteId}/calculation-sets/${setId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteSet(quoteId: string, setId: string): Promise<void> {
      await fetcher(`/quotes/${quoteId}/calculation-sets/${setId}`, { method: "DELETE" });
    },

    async approveSet(quoteId: string, setId: string): Promise<CalculationSetDetail> {
      const response = await fetcher<ApiSingleResponse<CalculationSetDetail>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/approve`,
        { method: "POST" },
      );
      return response.data;
    },

    /**
     * Mark a set as the primary scenario for the quote. Lightweight, distinct
     * from approve — does not lock siblings or roll totals to the quote.
     */
    async setPrimary(quoteId: string, setId: string): Promise<CalculationSetDetail> {
      const response = await fetcher<ApiSingleResponse<CalculationSetDetail>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/primary`,
        { method: "PUT" },
      );
      return response.data;
    },

    async updateSetStatus(
      quoteId: string,
      setId: string,
      payload: UpdateCalculationSetStatusDto,
    ): Promise<CalculationSetDetail> {
      const response = await fetcher<ApiSingleResponse<CalculationSetDetail>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/status`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    // ─── Set Cargos ──────────────────────────────────────────────────────
    async listSetCargos(quoteId: string, setId: string): Promise<CalculationSetCargosResponse> {
      const response = await fetcher<ApiSingleResponse<CalculationSetCargosResponse>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/cargos`,
      );
      return response.data;
    },

    async assignSetCargos(
      quoteId: string,
      setId: string,
      payload: AssignSetCargosPayload,
    ): Promise<unknown> {
      const response = await fetcher<ApiSingleResponse<unknown>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/cargos`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    // ─── Calculations ────────────────────────────────────────────────────
    async listCalculations(quoteId: string, setId: string): Promise<Calculation[]> {
      const response = await fetcher<ApiSingleResponse<Calculation[]>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations`,
      );
      return response.data ?? [];
    },

    async createCalculation(
      quoteId: string,
      setId: string,
      payload: CreateCalculationDto,
    ): Promise<Calculation> {
      const response = await fetcher<ApiSingleResponse<Calculation>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async getCalculation(
      quoteId: string,
      setId: string,
      calcId: string,
    ): Promise<Calculation> {
      const response = await fetcher<ApiSingleResponse<Calculation>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}`,
      );
      return response.data;
    },

    async updateCalculation(
      quoteId: string,
      setId: string,
      calcId: string,
      payload: UpdateCalculationDto,
    ): Promise<Calculation> {
      const response = await fetcher<ApiSingleResponse<Calculation>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteCalculation(
      quoteId: string,
      setId: string,
      calcId: string,
    ): Promise<{ calcSetId: string }> {
      const response = await fetcher<ApiSingleResponse<{ calcSetId: string }>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}`,
        { method: "DELETE" },
      );
      return response.data ?? { calcSetId: setId };
    },

    // ─── Trips ───────────────────────────────────────────────────────────
    async listTrips(
      quoteId: string,
      setId: string,
      calcId: string,
    ): Promise<CalculationTrip[]> {
      const response = await fetcher<ApiSingleResponse<CalculationTrip[]>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}/trips`,
      );
      return response.data ?? [];
    },

    async createTrips(
      quoteId: string,
      setId: string,
      calcId: string,
      payload: { trips: CalculationTripInput[] },
    ): Promise<unknown> {
      const response = await fetcher<ApiSingleResponse<unknown>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}/trips`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateTrip(
      quoteId: string,
      setId: string,
      calcId: string,
      tripId: string,
      payload: CalculationTripSyncInput,
    ): Promise<unknown> {
      const response = await fetcher<ApiSingleResponse<unknown>>(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}/trips/${tripId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteTrip(
      quoteId: string,
      setId: string,
      calcId: string,
      tripId: string,
    ): Promise<void> {
      await fetcher(
        `/quotes/${quoteId}/calculation-sets/${setId}/calculations/${calcId}/trips/${tripId}`,
        { method: "DELETE" },
      );
    },
  };
}
